import type { ScalperStar, StarSide, StarTier } from '../nq-scalper/signals';
import { underlyingPriceTicker } from '../../utils/tickerDisplay';
import { isRth } from './shareMinutes';

export interface StarCandle {
  time: number;
  high: number;
  low: number;
}

export interface ChartInterval {
  unit: 'minute' | 'hour' | 'day' | 'week' | 'month';
  count: number;
}

export interface PlacedStar {
  index: number;
  side: StarSide;
  tier: StarTier;
  slot: number;
}

export function starRadius(_tier: StarTier, size: number): number {
  return size;
}

/**
 * Path radius for one mark. A centered stroke draws half its weight outside
 * the path, so the hollow mark is inset by that half. The filled mark uses
 * the full radius. Both then share one outer edge.
 */
export function markerPathRadius(radius: number, filled: boolean, weight: number): number {
  if (filled || !(weight > 0)) return radius;
  return Math.max(weight / 2, radius - weight / 2);
}

export function starY(edgeY: number, side: StarSide, offsetPx: number): number {
  return side === 'short' ? edgeY - offsetPx : edgeY + offsetPx;
}

/** Five-point star. The path starts at the top point. Both tiers share one radius. */
export function starPath(cx: number, cy: number, outer = 6.5, inner = 2.7): string {
  let path = '';
  for (let point = 0; point < 10; point += 1) {
    const radius = point % 2 === 0 ? outer : inner;
    const angle = -Math.PI / 2 + (point * Math.PI) / 5;
    const x = cx + radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);
    path += point === 0 ? `M${x.toFixed(2)},${y.toFixed(2)}` : `L${x.toFixed(2)},${y.toFixed(2)}`;
  }
  return `${path}Z`;
}

function contains(candleTime: number, minute: number, nextTime: number): boolean {
  return minute >= candleTime && minute < nextTime;
}

/** Stars are an NQ signal drawn only on these cash charts. SPXW prices as SPX. */
const STAR_SYMBOLS = new Set(['SPX', 'SPY', 'QQQ']);

export function starsOnSymbol(symbol: string): boolean {
  return STAR_SYMBOLS.has(underlyingPriceTicker(symbol).trim().toUpperCase());
}

export function starsOnInterval(interval: ChartInterval): boolean {
  return interval.unit === 'minute' && interval.count === 1;
}

export function placeStars(
  stars: readonly ScalperStar[],
  candles: readonly StarCandle[],
  interval: ChartInterval,
  _offsetPx: number,
): PlacedStar[] {
  if (!starsOnInterval(interval)) return [];
  // NQ trades nearly around the clock. A chart with only cash-hour candles is
  // in Regular mode, and its last candle of each day would otherwise collect
  // every star until the next open.
  const regularOnly = candles.every((candle) => isRth(candle.time));
  const counts = new Map<number, number>();
  const out: PlacedStar[] = [];
  for (const star of stars) {
    if (regularOnly && !isRth(star.minute)) continue;
    let index = -1;
    for (let i = 0; i < candles.length; i += 1) {
      const next = i + 1 < candles.length ? candles[i + 1].time : Number.POSITIVE_INFINITY;
      if (contains(candles[i].time, star.minute, next)) {
        index = i;
        break;
      }
    }
    if (index < 0) continue;
    const slot = counts.get(index) ?? 0;
    counts.set(index, slot + 1);
    out.push({ index, side: star.side, tier: star.tier, slot });
  }
  return out;
}
